import { Globe, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import { useNetworkDiagnostics } from '../../api/queries';
import type { Network } from '../../api/types';
import { Loading } from '../../components/ui';
import { strings } from '../../strings';
import { useManageNetworks } from '../shared';
import type { GeneratedFlow } from './flows';

const r = strings.networks.requests;

/**
 * The Traffic tab's read-only part (spec 11 6.15.21, the "Segmentation" of feature spec 17 8.1): the generated flows,
 * grouped by deployment and written as "all apps of orders-prod in westeurope → db on TCP 5432", and the egress.
 */
export function TrafficFlows({ network, flows }: { network: Network; flows: GeneratedFlow[] | undefined }) {
  const aws = network.settings.providers.aws;
  const gcp = network.settings.providers.gcp;
  // AWS (feature spec 17, phase 4) and Google Cloud (phase 6) have their own egress parts below.
  const own = (provider: string) => provider === 'aws' || provider === 'gcp';
  const firewalls = Object.entries(network.settings.providers).filter(
    ([provider, p]) => !own(provider) && p?.firewall?.enabled,
  );
  const otherProviders = Object.keys(network.settings.providers).some((p) => !own(p));
  const withFirewall = firewalls.length > 0 || !!aws?.firewall?.enabled;
  const byDeployment = new Map<string, GeneratedFlow[]>();
  for (const f of flows ?? []) {
    const key = `${f.deploymentName}\n${f.region}`;
    byDeployment.set(key, [...(byDeployment.get(key) ?? []), f]);
  }
  return (
    <>
      <section aria-labelledby="traffic-flows" className="space-y-2" data-testid="generated-flows">
        <h2 id="traffic-flows" className="flex flex-wrap items-baseline gap-x-2 text-[15px] font-semibold">
          {r.generatedFlows}
          <span className="text-[12.5px] font-normal text-fg-muted">
            {r.generatedFlowsNote(flows?.length ?? 0)}
          </span>
        </h2>
        {!flows ? (
          <Loading />
        ) : flows.length === 0 ? (
          <p className="text-sm text-fg-muted">{r.noFlows}</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-line bg-surface">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{r.columns.sourceTarget}</th>
                  <th scope="col">{r.port}</th>
                  <th scope="col">{r.columns.details}</th>
                </tr>
              </thead>
              <tbody>
                {[...byDeployment.values()].flatMap((list) =>
                  list.map((f) => (
                    <tr key={`${f.deploymentId}/${f.region}/${f.sourceId}/${f.targetId}`}>
                      <td>
                        {r.flowSource(f.deploymentName, f.region)} →{' '}
                        <span className="font-mono">{f.targetId}</span>
                      </td>
                      <td className="font-mono">{f.port ? r.tcp(f.port) : strings.common.dash}</td>
                      <td className="text-[12.5px] text-fg-muted">
                        {r.flowWhy(f.sourceId, f.targetId)}
                        {withFirewall && ` · ${r.checkedByFirewall}`}
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section aria-labelledby="traffic-egress" className="space-y-2" data-testid="egress">
        <h2 id="traffic-egress" className="text-[15px] font-semibold">
          {r.egress}
        </h2>
        {aws && <AwsEgress network={network} />}
        {gcp && <GcpEgress network={network} />}
        {!otherProviders ? null : firewalls.length > 0 ? (
          firewalls.map(([provider, p]) => (
            <div key={provider} className="rounded-lg border border-line bg-surface p-3 text-sm">
              <p className="flex items-center gap-2">
                <Globe aria-hidden className="size-4 text-fg-muted" />
                {p?.firewall?.egress === 'through-firewall' ? r.egressThroughFirewall : r.egressDirect}
              </p>
              <p className="mt-1">
                {p?.firewall?.allowedEgress?.length
                  ? r.egressAllowed(p.firewall.allowedEgress.join(', '))
                  : r.egressNone}
              </p>
              <p className="help mt-1">{r.egressNote}</p>
            </div>
          ))
        ) : (
          <p className="callout callout-warning">
            <TriangleAlert aria-hidden className="size-4 shrink-0" />
            <span>
              {r.egressNotFiltered} <code className="font-mono text-xs">EGRESS_NOT_FILTERED</code>
            </span>
          </p>
        )}
      </section>
    </>
  );
}

/**
 * The AWS egress (spec 11 6.15.31): `EGRESS_NOT_FILTERED` until AWS Network Firewall filters egress; with central egress
 * the allowed host names, the central NAT gateways' public addresses and each `AWS_EGRESS_PUBLIC_SUBNETS` warning.
 */
function AwsEgress({ network }: { network: Network }) {
  const firewall = network.settings.providers.aws?.firewall;
  const central = !!firewall?.enabled && firewall.egress === 'through-firewall';
  const manage = useManageNetworks();
  const diagnostics = useNetworkDiagnostics(network.id, central && manage);
  if (!central)
    return (
      <p className="callout callout-warning" data-testid="aws-egress-not-filtered">
        <TriangleAlert aria-hidden className="size-4 shrink-0" />
        <span>
          {r.awsEgressNotFiltered} <code className="font-mono text-xs">EGRESS_NOT_FILTERED</code>
          {manage && (
            <>
              {' '}
              <Link className="link" to={`/networks/${network.id}?tab=settings#firewall`}>
                {r.turnOn}
              </Link>
            </>
          )}
        </span>
      </p>
    );
  const ips = Object.entries(network.outputs)
    .filter(([key]) => /^aws\/[^/]+\/hub$/.test(key))
    .flatMap(([, hub]) => {
      const list = (hub as { egressPublicIps?: unknown } | null)?.egressPublicIps;
      return Array.isArray(list) ? list.map(String) : [];
    });
  const warnings = (diagnostics.data ?? []).filter((d) => d.code === 'AWS_EGRESS_PUBLIC_SUBNETS');
  return (
    <div className="rounded-lg border border-line bg-surface p-3 text-sm" data-testid="aws-egress">
      <p className="flex items-center gap-2">
        <Globe aria-hidden className="size-4 text-fg-muted" />
        {ips.length ? r.awsCentralNat(ips.join(', ')) : r.awsCentralNatPending}
      </p>
      <p className="mt-1">
        {firewall?.allowedEgress?.length ? r.egressAllowed(firewall.allowedEgress.join(', ')) : r.egressNone}
      </p>
      {warnings.map((d, i) => (
        <p key={i} className="callout callout-warning mt-2" data-testid="aws-public-subnets-warning">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <span>
            {d.message} <code className="font-mono text-xs">AWS_EGRESS_PUBLIC_SUBNETS</code>
          </span>
        </p>
      ))}
    </div>
  );
}

/**
 * The Google Cloud egress (spec 11 6.15.35): `EGRESS_NOT_FILTERED` until Cloud NGFW filters egress; then the allowed host
 * names and Cloud NAT in each spoke whose apps need it (no central egress on Google Cloud).
 */
function GcpEgress({ network }: { network: Network }) {
  const firewall = network.settings.providers.gcp?.firewall;
  const filtered = !!firewall?.enabled && firewall.egress === 'through-firewall';
  const manage = useManageNetworks();
  const g = strings.networks.gcp;
  if (!filtered)
    return (
      <p className="callout callout-warning" data-testid="gcp-egress-not-filtered">
        <TriangleAlert aria-hidden className="size-4 shrink-0" />
        <span>
          {g.egressNotFiltered} <code className="font-mono text-xs">EGRESS_NOT_FILTERED</code>
          {manage && (
            <>
              {' '}
              <Link className="link" to={`/networks/${network.id}?tab=settings#firewall`}>
                {r.turnOn}
              </Link>
            </>
          )}
        </span>
      </p>
    );
  return (
    <div className="rounded-lg border border-line bg-surface p-3 text-sm" data-testid="gcp-egress">
      <p className="flex items-center gap-2">
        <Globe aria-hidden className="size-4 text-fg-muted" />
        {g.natEgress}
      </p>
      <p className="mt-1">
        {firewall?.allowedEgress?.length ? r.egressAllowed(firewall.allowedEgress.join(', ')) : r.egressNone}
      </p>
    </div>
  );
}
